/* Theme core: header, menu drawer, announcement bar and cart count. Light-DOM custom elements;
   server Liquid is the truth, this file only adds behaviour. Section code lives in its own module;
   entrance animation lives in motion.js, overlays in overlay.js, product cards in card.js.

   Header geometry, written on :root by <mw-header> (defaults in snippets/theme-tokens):
     --announcement-height  total height of the header-group sections above the header
     --header-height        height of the header bar at rest — the space it takes in the page
     --header-offset        bottom edge of the sticky header in the viewport while it is visible,
                            0px when the header is not sticky or not shown. Pin things with
                            top: var(--header-offset). */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
const root = document.documentElement;
const EDITOR_EVENTS = ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'];

const setPx = (name, value) => root.style.setProperty(name, `${Math.round(value)}px`);

/* Longest transition (duration + delay) currently declared on an element, in ms. */
function transitionTime(el) {
  const style = getComputedStyle(el);
  const ms = (list) => list.split(',').map((t) => (parseFloat(t) || 0) * (t.trim().endsWith('ms') ? 1 : 1000));
  const delays = ms(style.transitionDelay);
  return Math.max(0, ...ms(style.transitionDuration).map((d, i) => d + (delays[i % delays.length] || 0)));
}

/* ---------- Header: geometry, sticky state, dropdowns, mobile drawer ---------- */
class MwHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.site-header__bar');
    if (!this.bar) return;
    /* The sticky element is the section wrapper (schema class «section-site-header»). */
    this.host = this.closest('.shopify-section') || this;
    this.offset = -1;
    this.threshold = 8;
    this.onScroll = this.onScroll.bind(this);
    this.onEditorChange = this.onEditorChange.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.observe();
    this.measure();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    EDITOR_EVENTS.forEach((type) => document.addEventListener(type, this.onEditorChange));
    this.onScroll();
    this.setupDropdowns();
    this.setupDrawer();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocumentClick);
    EDITOR_EVENTS.forEach((type) => document.removeEventListener(type, this.onEditorChange));
    this.resizeObserver?.disconnect();
    /* Removed in the theme editor: nothing covers the page any more (unless a re-render
       already connected the replacement, or the mobile header is the one on duty). */
    if (!document.querySelector('mw-header, mw-mobile-header')) {
      setPx('--header-height', 0);
      setPx('--header-offset', 0);
    }
  }

  /* Header-group sections rendered before the header (announcement bars). */
  sectionsAbove() {
    const sections = [];
    for (let el = this.host.previousElementSibling; el; el = el.previousElementSibling) {
      if (el.classList.contains('shopify-section')) sections.push(el);
    }
    return sections;
  }

  observe() {
    this.resizeObserver.disconnect();
    this.resizeObserver.observe(this);
    this.sectionsAbove().forEach((section) => this.resizeObserver.observe(section));
  }

  onEditorChange() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.observe();
      this.measure();
    });
  }

  measure() {
    /* Below the «Mobile header breakpoint» that section hides this bar (display: none) and owns
       the geometry instead. An element with no client rects has nothing to report — writing 0
       here would fight <mw-mobile-header> for --header-height on every scroll frame. */
    if (!this.bar.getClientRects().length) return;
    const above = this.sectionsAbove().reduce((sum, section) => sum + section.offsetHeight, 0);
    this.threshold = above + 8;
    /* Floating as a box, the bar is shorter than at rest; layout wants the height at rest,
       which is the bar's own height unless the boxed minimum made it shorter. */
    const barHeight = this.bar.offsetHeight;
    const boxed = this.classList.contains('site-header--boxed') && this.classList.contains('is-scrolled');
    const restMin = parseFloat(getComputedStyle(this).getPropertyValue('--header-bar-min')) || 0;
    this.sticky = /sticky|fixed/.test(getComputedStyle(this.host).position);
    setPx('--announcement-height', above);
    setPx('--header-height', boxed ? Math.max(barHeight, restMin) : barHeight);
    this.updateOffset();
  }

  updateOffset() {
    if (!this.bar.getClientRects().length) return; /* hidden: the mobile header reports instead */
    let offset = 0;
    if (this.sticky && this.bar.getClientRects().length) {
      offset = Math.max(0, Math.round(this.bar.getBoundingClientRect().bottom));
    }
    if (offset === this.offset) return;
    this.offset = offset;
    setPx('--header-offset', offset);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      const scrolled = window.scrollY > this.threshold;
      if (scrolled !== this.classList.contains('is-scrolled')) {
        this.classList.toggle('is-scrolled', scrolled);
        this.follow();
      }
      this.updateOffset();
    });
  }

  /* The boxed bar slides into place; keep --header-offset on its edge until it settles. */
  follow() {
    this.followUntil = performance.now() + transitionTime(this.bar) + 50;
    if (this.following) return;
    this.following = true;
    const step = () => {
      this.updateOffset();
      if (this.isConnected && performance.now() < this.followUntil) requestAnimationFrame(step);
      else this.following = false;
    };
    requestAnimationFrame(step);
  }

  /* Dropdown panels: click or keyboard toggles, hover opens on pointer devices. */
  setupDropdowns() {
    this.triggers = [...this.querySelectorAll('[data-mega-trigger]')];
    this.triggers.forEach((trigger) => {
      const item = trigger.closest('.nav-item');
      if (!item || !this.panelFor(trigger)) return;
      let timer;
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (trigger.getAttribute('aria-expanded') === 'true') this.closePanel(trigger);
        else this.openPanel(trigger);
      });
      item.addEventListener('pointerenter', () => {
        if (!canHover.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => this.openPanel(trigger), 60);
      });
      item.addEventListener('pointerleave', () => {
        if (!canHover.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => this.closePanel(trigger), 140);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) this.closePanel(trigger);
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
          this.closePanel(trigger);
          trigger.focus();
        }
      });
    });
    document.addEventListener('click', this.onDocumentClick);

    /* Theme editor: selecting a «Mega menu» block shows its panel. */
    this.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest?.('.mega');
      const trigger = panel && this.triggers.find((t) => t.getAttribute('aria-controls') === panel.id);
      if (trigger) this.openPanel(trigger);
    });
    this.addEventListener('shopify:block:deselect', () => this.closeAll());
  }

  panelFor(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  openPanel(trigger) {
    this.closeAll(trigger);
    trigger.setAttribute('aria-expanded', 'true');
    this.panelFor(trigger)?.classList.add('is-open');
  }

  closePanel(trigger) {
    trigger.setAttribute('aria-expanded', 'false');
    this.panelFor(trigger)?.classList.remove('is-open');
  }

  closeAll(except) {
    this.triggers?.forEach((trigger) => {
      if (trigger !== except) this.closePanel(trigger);
    });
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.closeAll();
  }

  /* Mobile menu: a native <dialog class="drawer"> (Escape and focus trap are built in). */
  setupDrawer() {
    const button = this.querySelector('[data-menu-open]');
    const drawer = document.getElementById(button?.getAttribute('aria-controls') || 'MenuDrawer');
    if (!button || !drawer) return;
    button.addEventListener('click', () => {
      drawer.showModal();
      button.setAttribute('aria-expanded', 'true');
    });
    drawer.addEventListener('close', () => {
      button.setAttribute('aria-expanded', 'false');
      button.focus();
    });
  }
}
if (!customElements.get('mw-header')) customElements.define('mw-header', MwHeader);

/* ---------- Drawer dialogs: close buttons + click on the backdrop ----------
   Only the legacy dialog.drawer; overlays (dialog.overlay) are handled by overlay.js. */
document.addEventListener('click', (event) => {
  const closer = event.target.closest('[data-dialog-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open || dialog.classList.contains('overlay')) return;
  const rect = dialog.getBoundingClientRect();
  const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
  if (!inside) dialog.close();
});

/* ---------- Announcement: message slider ----------
   Autoplay pauses on hover, focus and while a message block is selected in the theme editor,
   never starts under reduced motion, and stops for good once the visitor uses the arrows
   (WAI-ARIA carousel pattern; the stop mechanism WCAG 2.2.2 asks for). Hidden messages are inert. */
class MwAnnouncement extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.querySelectorAll('.announcement__message')];
    if (this.messages.length < 2) return;
    this.index = Math.max(0, this.messages.findIndex((message) => message.classList.contains('is-active')));
    this.delay = Number(this.dataset.autoplay || 0) * 1000;
    this.messages.forEach((message, i) => {
      message.inert = i !== this.index;
    });
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
    /* WCAG 2.2.2: the arrows stop autoplay for good, but the shopper needs a visible switch. */
    this.toggle = this.querySelector('[data-toggle]');
    this.toggle?.addEventListener('click', () => {
      this.hold('stopped', !this.stopped);
      this.syncToggle();
    });
    this.addEventListener('pointerenter', () => this.hold('hovered', true));
    this.addEventListener('pointerleave', () => this.hold('hovered', false));
    this.addEventListener('focusin', () => this.hold('focused', true));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.hold('focused', false);
    });
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.messages.indexOf(event.target.closest?.('.announcement__message'));
      if (index >= 0) this.go(index);
      this.hold('selected', true);
    });
    this.addEventListener('shopify:block:deselect', () => this.hold('selected', false));
    this.syncToggle();
    this.onMotionChange = () => this.start();
    reducedMotion.addEventListener('change', this.onMotionChange);
    this.start();
  }

  hold(reason, on) {
    this[reason] = on;
    this.start();
  }

  syncToggle() {
    if (!this.toggle) return;
    const paused = !!this.stopped;
    this.toggle.setAttribute('aria-label', this.toggle.dataset[paused ? 'labelPlay' : 'labelPause'] || '');
    this.toggle.classList.toggle('is-paused', paused);
    this.toggleAttribute('data-paused', paused);
  }

  go(next, user) {
    const count = this.messages.length;
    this.messages[this.index].classList.remove('is-active');
    this.messages[this.index].inert = true;
    this.index = (next + count) % count;
    this.messages[this.index].classList.add('is-active');
    this.messages[this.index].inert = false;
    if (user) {
      this.hold('stopped', true);
      this.syncToggle();
    }
  }

  start() {
    this.stop();
    if (!this.delay || this.hovered || this.focused || this.selected || this.stopped || reducedMotion.matches) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.delay);
  }

  stop() {
    clearInterval(this.timer);
  }

  disconnectedCallback() {
    this.stop();
    if (this.onMotionChange) reducedMotion.removeEventListener('change', this.onMotionChange);
  }
}
if (!customElements.get('mw-announcement')) customElements.define('mw-announcement', MwAnnouncement);

/* ---------- Cart count (cart:updated carries the cart JSON from /cart.js) ---------- */
document.addEventListener('cart:updated', (event) => {
  const count = event.detail?.item_count;
  if (typeof count !== 'number') return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.hidden = count === 0;
    const label = el.querySelector('span');
    if (label) label.textContent = count;
  });
});

export { reducedMotion, canHover };
